import { useEffect, useState } from "react";
// templates in ascii format for the keyboard shortcuts
export const formatKeyboardShortcut = (shortcut)=>{
    return shortcut.map((key)=>{
        if (key === "ctrl") return "⌃";
        if (key === "shift") return "⇧";
        if (key === "alt") return "⌥";
        if (key === "cmd") return "⌘";
        if (key === "enter") return "↵";
        if (key === "backspace") return "⌫";
        return key.toUpperCase();
    });
};
const keyToCode = (key)=>{
    // Handle special cases first
    const specialKeys = {
        backspace: "Backspace",
        enter: "Enter",
        space: "Space",
        del: "Delete",
        "-": "Minus",
        "=": "Equal",
        "[": "BracketLeft",
        "]": "BracketRight",
        "\\": "Backslash",
        ";": "Semicolon",
        "'": "Quote",
        ",": "Comma",
        ".": "Period",
        "/": "Slash"
    };
    if (specialKeys[key]) return specialKeys[key];
    // Handle numbers
    if (/^[0-9]$/.test(key)) return `Digit${key}`;
    // Handle letters
    if (/^[a-z]$/.test(key)) return `Key${key.toUpperCase()}`;
    return key;
};
// useCommandKey - a hook that returns true while the command key is pressed
export const useCommandKey = ()=>{
    const [isCommandPressed, setIsCommandPressed] = useState(false);
    useEffect(()=>{
        const handleKeyDown = (event)=>{
            if (event.key === "Meta") {
                setIsCommandPressed(true);
            }
        };
        const handleKeyUp = (event)=>{
            if (event.key === "Meta") {
                setIsCommandPressed(false);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        return ()=>{
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
        };
    }, []);
    // when the browser loses focus, the keyup event is not fired, so we need to check if the command key is pressed
    useEffect(()=>{
        const handleBlur = ()=>{
            setIsCommandPressed(false);
        };
        window.addEventListener("blur", handleBlur);
        return ()=>{
            window.removeEventListener("blur", handleBlur);
        };
    }, []);
    return {
        isCommandPressed,
        setCommandPressed: setIsCommandPressed
    };
};
export const useKeyPress = (targetKey)=>{
    const [isKeyPressed, setIsKeyPressed] = useState(false);
    useEffect(()=>{
        const handleKeyDown = (event)=>{
            if (event.key === targetKey) {
                setIsKeyPressed(true);
            }
        };
        const handleKeyUp = (event)=>{
            if (event.key === targetKey) {
                setIsKeyPressed(false);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        return ()=>{
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
        };
    }, [
        targetKey
    ]) // Added targetKey as a dependency
    ;
    // Handles case when the browser loses focus
    useEffect(()=>{
        const handleBlur = ()=>{
            setIsKeyPressed(false);
        };
        window.addEventListener("blur", handleBlur);
        return ()=>{
            window.removeEventListener("blur", handleBlur);
        };
    }, []);
    return {
        isKeyPressed,
        setKeyPressed: setIsKeyPressed
    };
};
export const useHotKey = ({ hotkey: shortcutArray, action, disabled, allowInInputs = false })=>{
    useEffect(()=>{
        const shortcut = shortcutArray?.join("+").toLowerCase();
        if (disabled) return; // Exit if the shortcut is disabled
        if (!shortcut) return; // Exit if no shortcut is provided
        if (typeof window === "undefined") return; // Exit if we're not in the browser
        const handleKeyPress = async (event)=>{
            // Ignore shortcuts if user is typing in an input field
            const activeElement = document.activeElement;
            if (!allowInInputs && activeElement instanceof HTMLElement && (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA" || activeElement.isContentEditable)) {
                return;
            }
            const keys = shortcut.toLowerCase().split("+").map((key)=>key.trim());
            let requiredKeys = {
                ctrl: keys.includes("ctrl"),
                shift: keys.includes("shift"),
                alt: keys.includes("alt"),
                cmd: keys.includes("cmd"),
                code: keyToCode(keys.find((key)=>![
                        "ctrl",
                        "shift",
                        "alt",
                        "cmd"
                    ].includes(key)) || "")
            };
            if (event.ctrlKey === requiredKeys.ctrl && event.shiftKey === requiredKeys.shift && event.altKey === requiredKeys.alt && event.metaKey === requiredKeys.cmd && event.code === requiredKeys.code) {
                event.preventDefault();
                // event.stopImmediatePropagation()
                // event.stopPropagation()
                await action();
            }
        };
        window.addEventListener("keydown", handleKeyPress);
        return ()=>{
            window.removeEventListener("keydown", handleKeyPress);
        };
    }, [
        shortcutArray,
        action,
        disabled
    ]);
};
export default useHotKey;
